import { AppModal, Button, Text } from "@/components/ui";
import { SortableMatterStatusRow } from "./SortableMatterStatusRow";
import { useUpdateMatterStatusOrder } from "@/lib/data/react-query/matters/matter-status";
import type { MatterStatus } from "@/lib/types";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useState } from "react";

export function ReorderMatterStatusesModal({
  categoryId,
  categoryLabel,
  statuses,
  onClose,
  onRestoreFocus,
}: {
  categoryId: string;
  categoryLabel: string;
  statuses: MatterStatus[];
  onClose: () => void;
  onRestoreFocus: () => void;
}) {
  const [draft, setDraft] = useState(statuses);
  const updateOrder = useUpdateMatterStatusOrder();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (updateOrder.isPending || !over || active.id === over.id) return;
    setDraft((current) => {
      const from = current.findIndex((status) => status.id === active.id);
      const to = current.findIndex((status) => status.id === over.id);
      return from < 0 || to < 0 ? current : arrayMove(current, from, to);
    });
  };

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open && !updateOrder.isPending) onClose();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        onRestoreFocus();
      }}
      title={`Reorder ${categoryLabel.toLowerCase()} statuses`}
      size="lg"
      loading={updateOrder.isPending}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="outline"
            disabled={updateOrder.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="foreground"
            disabled={updateOrder.isPending}
            onClick={() => {
              updateOrder.mutate(
                {
                  categoryId,
                  statuses: draft.map((status, index) => ({
                    id: status.id,
                    order: index + 1,
                    dateModified: status.dateModified,
                  })),
                },
                { onSuccess: onClose },
              );
            }}
          >
            Save order
          </Button>
        </div>
      }
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={draft.map((status) => status.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="divide-border divide-y px-6">
            {draft.map((status) => (
              <SortableMatterStatusRow
                key={status.id}
                id={status.id}
                label={status.label}
                canReorder
                isReordering={updateOrder.isPending}
              >
                <Text variant="body" className="py-1.5">
                  {status.label}
                </Text>
              </SortableMatterStatusRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </AppModal>
  );
}
